import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUserDetails } from '../../redux/userRelated/userHandle';
import { getStudentMarks } from '../../redux/marksRelated/marksHandle';
import { formatDatePart } from '../../utils/customDate';
import { 
    Container, Typography, Box, Paper, Grid, 
    LinearProgress, Chip, Avatar, Fade, Zoom, 
    Stack, Tab, Tabs,  
    CircularProgress,  Button
} from '@mui/material';
import { 
    Assessment as AssessmentIcon, 
    TrendingUp as TrendingUpIcon,
    EmojiEvents as TrophyIcon,
    CheckCircle as PassIcon,
    Warning as FailIcon,
    School as SubjectIcon,
    CalendarMonth as DateIcon,
    Info as InfoIcon,
    History as HistoryIcon,
    Star as GradeIcon
} from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 100px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const GlassCard = styled(Paper)`
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
`;

const PerformanceCard = styled(Paper)`
    padding: 24px;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: white;
    border: 1px solid #eef2ff;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -10px rgba(99, 102, 241, 0.15);
    }
`;

const SubjectProgress = styled(LinearProgress)`
    height: 10px !important;
    border-radius: 5px !important;
    background-color: #f1f5f9 !important;
    & .MuiLinearProgress-bar {
        border-radius: 5px !important;
        background: ${props => props.percentage >= 80 
            ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)' 
            : props.percentage >= 40 
            ? 'linear-gradient(90deg, #6366f1 0%, #818cf8 100%)' 
            : 'linear-gradient(90deg, #ef4444 0%, #f87171 100%)'};
    }
`;

const ExamTab = styled(Tab)`
    text-transform: none !important;
    font-weight: 700 !important;
    font-size: 0.95rem !important;
    min-width: 140px !important;
    border-radius: 12px !important;
    margin: 0 4px !important;
    transition: all 0.2s ease !important;
    color: #64748b !important;
    
    &.Mui-selected {
        color: #6366f1 !important;
        background: rgba(99, 102, 241, 0.08) !important;
    }
`;

const StudentResults = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useSelector((state) => state.user);
    const { marksList, loading } = useSelector((state) => state.marks);
    
    const [examType, setExamType] = useState('all');

    useEffect(() => {
        dispatch(getUserDetails(currentUser._id, "Student"));
    }, [dispatch, currentUser._id]);

    useEffect(() => {
        if (currentUser?._id) {
            dispatch(getStudentMarks(currentUser._id, examType));
        }
    }, [dispatch, currentUser._id, examType]);

    const filteredMarks = useMemo(() => {
        if (!marksList) return [];
        if (location.state?.filterBySubject) {
            return marksList.filter(m => m.subject?.subName === location.state.filterBySubject);
        }
        return marksList;
    }, [marksList, location.state]);

    const examTypes = useMemo(() => {
        if (!marksList) return [];
        return [...new Set(marksList.map(m => m.examType))];
    }, [marksList]);

    const stats = useMemo(() => {
        if (!filteredMarks || filteredMarks.length === 0) return null;
        
        const totalMarks = filteredMarks.reduce((acc, curr) => acc + (curr.marksObtained || 0), 0);
        const totalMaxMarks = filteredMarks.reduce((acc, curr) => acc + (curr.maxMarks || 0), 0);
        const percentage = totalMaxMarks > 0 ? (totalMarks / totalMaxMarks) * 100 : 0;
        const passedCount = filteredMarks.filter(m => ((m.marksObtained || 0) / (m.maxMarks || 1)) * 100 >= 40).length;
        
        return {
            totalMarks,
            totalMaxMarks,
            percentage,
            passedCount,
            totalExams: filteredMarks.length,
            failedCount: filteredMarks.length - passedCount
        };
    }, [filteredMarks]);

    const getGradeInfo = (percentage) => {
        if (percentage >= 90) return { grade: 'A+', color: '#10b981', bg: '#ecfdf5', status: 'Excellent' };
        if (percentage >= 80) return { grade: 'A', color: '#059669', bg: '#ecfdf5', status: 'Very Good' };
        if (percentage >= 70) return { grade: 'B+', color: '#6366f1', bg: '#eef2ff', status: 'Good' };
        if (percentage >= 60) return { grade: 'B', color: '#4f46e5', bg: '#eef2ff', status: 'Above Average' };
        if (percentage >= 50) return { grade: 'C+', color: '#f59e0b', bg: '#fff7ed', status: 'Average' };
        if (percentage >= 40) return { grade: 'C', color: '#d97706', bg: '#fff7ed', status: 'Pass' };
        return { grade: 'F', color: '#dc2626', bg: '#fef2f2', status: 'Fail' };
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={50} thickness={4} />
            </Box>
        );
    }

    const gradeInfo = stats ? getGradeInfo(stats.percentage) : null;

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="xl">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                                    <AssessmentIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                        Academic Results
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                        {location.state?.filterBySubject ? `Showing performance for ${location.state.filterBySubject}` : 'Track your examination scores and performance metrics.'}
                                    </Typography>
                                </Box>
                            </Box>
                            
                            {stats && (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Box sx={{ textAlign: 'right', bgcolor: 'rgba(255,255,255,0.1)', p: 2, borderRadius: '20px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                                        <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Overall Percentage</Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 900, color: gradeInfo.color }}>
                                            {stats.percentage.toFixed(1)}%
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center', bgcolor: gradeInfo.bg, p: 2, borderRadius: '20px', minWidth: '100px', border: `1px solid ${gradeInfo.color}20` }}>
                                        <Typography variant="caption" sx={{ color: gradeInfo.color, fontWeight: 800, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Final Grade</Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 900, color: gradeInfo.color }}>
                                            {gradeInfo.grade}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="xl" sx={{ position: "relative", mt: 3, mb: 4 }}>
                {/* Exam Filter Tabs */}
                {!location.state?.filterBySubject && (
                    <Box sx={{ mb: 4, bgcolor: 'white', p: 1, borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                        <Tabs 
                            value={examType} 
                            onChange={(e, v) => setExamType(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ '& .MuiTabs-indicator': { display: 'none' } }}
                        >
                            <ExamTab label="All Examinations" value="all" />
                            {examTypes.map(type => (
                                <ExamTab key={type} label={type} value={type} />
                            ))}
                        </Tabs>
                    </Box>
                )}

                {stats ? (
                    <Grid container spacing={3}>
                        {/* Summary Section */}
                        <Grid item xs={12} md={3}>
                            <Stack spacing={2}>
                                <PerformanceCard elevation={0}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6' }}><TrophyIcon fontSize="small" /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>TOTAL SCORE</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 900 }}>{stats.totalMarks} / {stats.totalMaxMarks}</Typography>
                                        </Box>
                                    </Box>
                                </PerformanceCard>
                                <PerformanceCard elevation={0}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: '#f0fdf4', color: '#10b981' }}><PassIcon fontSize="small" /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>PASSED SUBJECTS</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 900 }}>{stats.passedCount} Subjects</Typography>
                                        </Box>
                                    </Box>
                                </PerformanceCard>
                                {stats.failedCount > 0 && (
                                    <PerformanceCard elevation={0}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: '#fef2f2', color: '#ef4444' }}><FailIcon fontSize="small" /></Avatar>
                                            <Box>
                                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>FAILED SUBJECTS</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#dc2626' }}>{stats.failedCount} Subjects</Typography>
                                            </Box>
                                        </Box>
                                    </PerformanceCard>
                                )}
                                <PerformanceCard elevation={0} sx={{ bgcolor: '#1e293b !important', color: 'white' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.8 }}>REMARKS</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 900 }}>{gradeInfo.status}</Typography>
                                </PerformanceCard>
                                {location.state?.filterBySubject && (
                                    <Button 
                                        variant="outlined" 
                                        onClick={() => navigate('/Student/subjects')}
                                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                                    >
                                        Back to Roadmap
                                    </Button>
                                )}
                            </Stack>
                        </Grid>

                        {/* Subject Results Stack */}
                        <Grid item xs={12} md={9}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <Avatar sx={{ bgcolor: '#f5f3ff', color: '#8b5cf6' }}><HistoryIcon /></Avatar>
                                <Typography variant="h5" sx={{ fontWeight: 800 }}>Subject Performance Breakdown</Typography>
                            </Box>
                            
                            <Grid container spacing={3}>
                                {filteredMarks.map((mark, index) => {
                                    const percentage = ((mark.marksObtained || 0) / (mark.maxMarks || 1)) * 100;
                                    const subGrade = getGradeInfo(percentage);
                                    
                                    return (
                                        <Grid item xs={12} sm={6} key={index}>
                                            <Fade in timeout={500 + index * 100}>
                                                <GlassCard sx={{ p: 3, position: 'relative' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Avatar sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b' }}>
                                                                <SubjectIcon fontSize="small" />
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                                                                    {mark.subject?.subName || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                                                                    {mark.examType} • {formatDatePart(mark.examDate)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Chip 
                                                            label={subGrade.grade} 
                                                            size="small" 
                                                            sx={{ bgcolor: subGrade.bg, color: subGrade.color, fontWeight: 900, borderRadius: '8px' }} 
                                                        />
                                                    </Box>
                                                    
                                                    <Box sx={{ mb: 2 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569' }}>
                                                                Score: {mark.marksObtained} / {mark.maxMarks}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 800, color: subGrade.color }}>
                                                                {percentage.toFixed(1)}%
                                                            </Typography>
                                                        </Box>
                                                        <SubjectProgress variant="determinate" value={Math.min(percentage, 100)} percentage={percentage} />
                                                    </Box>

                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.8 }}>
                                                        {percentage >= 40 ? <PassIcon sx={{ color: '#10b981', fontSize: 16 }} /> : <FailIcon sx={{ color: '#ef4444', fontSize: 16 }} />}
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: percentage >= 40 ? '#059669' : '#dc2626' }}>
                                                            {percentage >= 40 ? 'Requirement Met' : 'Needs Improvement'}
                                                        </Typography>
                                                    </Stack>
                                                </GlassCard>
                                            </Fade>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Grid>
                    </Grid>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 12 }}>
                        <Avatar sx={{ width: 80, height: 80, bgcolor: '#f1f5f9', color: '#94a3b8', mx: 'auto', mb: 2 }}>
                            <AssessmentIcon sx={{ fontSize: 40 }} />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>No specific results for {location.state?.filterBySubject}</Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Try selecting a different subject or viewing all results.</Typography>
                        <Button 
                            variant="contained" 
                            onClick={() => navigate('/Student/subjects')}
                            sx={{ mt: 3, borderRadius: '12px', textTransform: 'none' }}
                        >
                            Back to Roadmap
                        </Button>
                    </Box>
                )}
            </ContentContainer>
        </PageWrapper>
    );
};

export default StudentResults;
